import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Rocket } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { HelpCenter, useTour } from '@nucleus-commerce/admin-kit/tour';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStoreReadiness } from '@/features/stores';

import { ADMIN_TOUR_GROUPS } from '../definitions';

const ROUTE = '/_authed/_active/help' as const;

/** Links to the launch checklist (never copies its items) while the store has blockers. */
function LaunchCallout({ storeId }: { storeId: string }) {
  const { t } = useTranslation('tours');
  const { can } = usePermissions();
  const readiness = useStoreReadiness(storeId, can('stores.read'));
  const blockers = readiness.data?.checks.filter((c) => c.severity === 'blocker' && !c.ok) ?? [];
  if (!readiness.data || readiness.data.ready || blockers.length === 0) return null;
  return (
    <Alert>
      <Rocket aria-hidden />
      <AlertTitle>{t('page.launch.title')}</AlertTitle>
      <AlertDescription className="flex flex-col items-start gap-2">
        {t('page.launch.description')}
        <Button asChild size="sm" variant="outline">
          <Link to="/s/$storeId" params={{ storeId }}>
            {t('page.launch.action')}
          </Link>
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function Help() {
  const { tour: deepLink } = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: '/help' });
  const { storeId } = useTour();
  const clearDeepLink = useCallback(
    () => navigate({ search: (prev) => ({ ...prev, tour: undefined }), replace: true }),
    [navigate],
  );
  return (
    <HelpCenter
      groups={ADMIN_TOUR_GROUPS}
      targets={{
        progress: 'help.center.progress',
        search: 'help.center.search',
        groups: 'help.center.groups',
        restart: 'help.center.restart',
      }}
      deepLinkTourId={deepLink}
      onDeepLinkHandled={clearDeepLink}
      callout={storeId ? <LaunchCallout storeId={storeId} /> : null}
    />
  );
}

export function HelpCenterPage() {
  return (
    <FeatureErrorBoundary name="tours.help">
      <Help />
    </FeatureErrorBoundary>
  );
}
